import { track } from '@/lib/analytics/track';
import { cn } from '@/lib/cn';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { Separator } from '@heidi/design-system/separator';
import { TypographyCaption } from '@heidi/design-system/typography';
import { LayoutGrid, SlashSquare, Zap } from 'lucide-react';
import type { JSX, JSXElementConstructor, ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { PinnedChip } from './evidence-settings-popover';
import type { CuratedProtocol } from './protocols-button.types';
import type { ResolvedShortcut } from './shortcut-marketplace-data';
import { ShortcutSaveToggle } from './shortcut-save-toggle';

const messages = defineMessages({
  protocols: {
    defaultMessage: 'Protocols',
    id: 'NI+3Dmiklq',
    description: 'Heading for curated protocol rows in the shortcuts menu',
  },
  seeAll: {
    defaultMessage: 'See all shortcuts',
    id: 'dNJRnkljvn',
    description: 'Opens the full shortcuts marketplace from the shortcuts menu',
  },
});

const ITEM_CLASS = 'group cursor-pointer';

export type ShortcutMenuItemProps = {
  children?: ReactNode;
  onClick?: () => void;
  className?: string;
  accessoryEnd?: ReactNode;
  'data-testid'?: string;
  'data-shortcut-label'?: string;
};

type ShortcutMenuItem = JSXElementConstructor<ShortcutMenuItemProps>;

interface ShortcutMenuListProps {
  Item: ShortcutMenuItem;
  pinnedChips: readonly PinnedChip[];
  onPinnedChipClick?: (chip: PinnedChip) => void;
  onUnsaveShortcut?: (chip: PinnedChip) => void;
  recommendedShortcuts?: readonly ResolvedShortcut[];
  onRecommendedSelect?: (shortcut: ResolvedShortcut) => void;
  onSaveShortcut?: (shortcut: ResolvedShortcut) => void;
  isAtShortcutLimit?: boolean;
  activeCuratedProtocols?: readonly CuratedProtocol[];
  onProtocolSelect?: (shorthand: string) => void;
}

const runSaved = (chip: PinnedChip, onPinnedChipClick?: (chip: PinnedChip) => void) => {
  track({
    event_name: TrackingEventName.EvidenceShortcutRun,
    props: {
      shortcut_source: 'saved',
      via: 'click',
      shortcut_id: chip.id,
    },
    options: { amplitude: false },
  });
  onPinnedChipClick?.(chip);
};

const runRecommended = (
  shortcut: ResolvedShortcut,
  onRecommendedSelect?: (shortcut: ResolvedShortcut) => void,
  onPinnedChipClick?: (chip: PinnedChip) => void
) => {
  track({
    event_name: TrackingEventName.EvidenceShortcutRun,
    props: {
      shortcut_source: 'recommended',
      via: 'click',
      shortcut_id: shortcut.id,
    },
    options: { amplitude: false },
  });
  if (onRecommendedSelect) {
    onRecommendedSelect(shortcut);
    return;
  }
  onPinnedChipClick?.({
    id: shortcut.id,
    label: shortcut.label,
    query: shortcut.query,
    category: shortcut.category,
  });
};

/**
 * Pinned, recommended, and curated rows shared by the Ask Heidi dropdown and
 * the in-session `+` flyout. The row chrome is injected so a dropdown item and
 * a plain button can share one list.
 */
export const ShortcutMenuList = ({
  Item,
  pinnedChips,
  onPinnedChipClick,
  onUnsaveShortcut,
  recommendedShortcuts = [],
  onRecommendedSelect,
  onSaveShortcut,
  isAtShortcutLimit = false,
  activeCuratedProtocols = [],
  onProtocolSelect,
}: ShortcutMenuListProps): JSX.Element => {
  const intl = useIntl();
  const hasPinned = pinnedChips.length > 0;
  const hasRecommended = recommendedShortcuts.length > 0;
  const hasCurated = activeCuratedProtocols.length > 0 && Boolean(onProtocolSelect);

  return (
    <>
      {pinnedChips.map((chip) => (
        <Item
          key={chip.id}
          onClick={() => runSaved(chip, onPinnedChipClick)}
          className={cn(ITEM_CLASS, 'flex w-full items-center gap-2')}
          data-testid="evidence-shortcuts-pinned-item"
          data-shortcut-label={chip.label}
          accessoryEnd={
            onUnsaveShortcut ? (
              <ShortcutSaveToggle isSaved onToggle={() => onUnsaveShortcut(chip)} />
            ) : undefined
          }
        >
          <SlashSquare size={14} className="text-text-secondary" />
          <span className="truncate">{chip.label}</span>
        </Item>
      ))}

      {hasRecommended ? (
        <>
          {hasPinned ? (
            <Separator className="my-1" data-testid="shortcut-menu-separator" />
          ) : null}
          {recommendedShortcuts.map((shortcut) => (
            <Item
              key={shortcut.id}
              onClick={() =>
                runRecommended(shortcut, onRecommendedSelect, onPinnedChipClick)
              }
              className={cn(ITEM_CLASS, 'flex w-full items-center gap-2')}
              data-testid="evidence-shortcuts-recommended-item"
              data-shortcut-label={shortcut.label}
              accessoryEnd={
                onSaveShortcut ? (
                  <ShortcutSaveToggle
                    isSaved={false}
                    disabled={isAtShortcutLimit}
                    onToggle={() => onSaveShortcut(shortcut)}
                  />
                ) : undefined
              }
            >
              <SlashSquare size={14} className="text-text-secondary" />
              <span className="truncate">{shortcut.label}</span>
            </Item>
          ))}
        </>
      ) : null}

      {hasCurated ? (
        <>
          <Separator className="my-1" data-testid="shortcut-menu-separator" />
          <TypographyCaption className="px-2 py-1.5 text-text-tertiary">
            {intl.formatMessage(messages.protocols)}
          </TypographyCaption>
          {activeCuratedProtocols.map((protocol) => (
            <Item
              key={protocol.id}
              onClick={() => onProtocolSelect?.(protocol.shorthand)}
              className={cn(ITEM_CLASS, 'flex w-full items-center gap-2')}
              data-testid="evidence-shortcuts-protocol-item"
            >
              <Zap size={14} className="text-text-secondary" />
              <span className="truncate">{protocol.name}</span>
            </Item>
          ))}
        </>
      ) : null}
    </>
  );
};

export const ShortcutMenuBrowseAll = ({
  Item,
  onBrowse,
}: {
  Item: ShortcutMenuItem;
  onBrowse: () => void;
}): JSX.Element => {
  const intl = useIntl();

  return (
    <Item
      onClick={onBrowse}
      className="flex w-full cursor-pointer items-center gap-2"
      data-testid="evidence-shortcuts-browse-all"
    >
      <LayoutGrid size={14} className="text-text-secondary" />
      <span>{intl.formatMessage(messages.seeAll)}</span>
    </Item>
  );
};
